import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import type { ReengagementEligibility } from '../types/dischargedReengagement';

/**
 * Batch eligibility for the discharged re-engagement gate. Calls
 * getReengagementEligibility for the visible loanIds and returns a per-loan map.
 * Only active when `enabled` (the tenant feature flag) is true, so a tenant
 * without the feature pays no callable cost.
 *
 * The actual client email is withheld by the server until a loan is eligible, so
 * nothing sensitive is cached here pre-eligibility.
 */

interface EligibilityResponse {
  hasAccess: boolean;
  enabled: boolean;
  results: ReengagementEligibility[];
}

export function useReengagementEligibility(companyId: string | undefined, loanIds: string[]) {
  const [byLoan, setByLoan] = useState<Record<string, ReengagementEligibility>>({});
  const [hasAccess, setHasAccess] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);

  // Stable key so the effect only refires when the actual set of loans changes.
  const loanKey = loanIds.slice().sort().join(',');

  const refetch = useCallback(async () => {
    if (!companyId || loanIds.length === 0) {
      setByLoan({});
      setEnabled(false);
      return;
    }
    setLoading(true);
    try {
      const fn = httpsCallable<{ companyId: string; loanIds: string[] }, EligibilityResponse>(
        functions,
        'getReengagementEligibility',
      );
      const res = await fn({ companyId, loanIds });
      const map: Record<string, ReengagementEligibility> = {};
      for (const r of res.data.results ?? []) map[r.loanId] = r;
      setByLoan(map);
      setHasAccess(res.data.hasAccess !== false);
      setEnabled(res.data.enabled === true);
    } catch (e) {
      console.error('[useReengagementEligibility] load failed:', e);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, loanKey]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { byLoan, hasAccess, enabled, loading, refetch };
}
